import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '../components/ui/accordion';
import { HelpCircle, MessageCircle, Mail } from 'lucide-react';
import SEOHead, { pageSEO, generateFAQSchema, generateBreadcrumbSchema } from '../components/SEOHead';

const FAQPage = () => {
    const faqs = [
        {
            question: "How old does my dog need to be to start Mantrailing?",
            answer: `There's no strict age requirement—puppies through to senior dogs are all welcome. Trails can be adapted to suit each individual dog. Mantrailing is a low-impact sport that provides excellent mental stimulation, making it suitable for dogs of any age.

That said, very young puppies may need someone to stay with them while waiting in the car between trails.`
        },
        {
            question: "I have multiple dogs that I'd like to start Mantrailing with. Do I need to do an introduction workshop with both of them?",
            answer: `At Craven Canines, we would argue no—the introduction workshop is as much about your learning as it is your dog's. Once you've completed an introduction workshop with one dog, you can book your other dog onto a regular training session.

You're also very welcome to do an introduction workshop with both dogs if you prefer. Please note that other instructors may have different policies regarding multiple dogs.`
        },
        {
            question: "My dog can't be left alone in the car. Will this be a problem?",
            answer: `Generally, this isn't an issue. You can usually stay with your dog between trails. The only downside is that you may miss some learning opportunities, as watching other dogs work and taking turns hiding provides valuable experience.

You're also welcome to bring an additional person to sit with your dog in-between trails.

Most dogs quickly adapt to waiting between trails, and it often becomes a welcome rest for them.`
        },
        {
            question: "My dog never wears a harness. Do they really need one just to try Mantrailing?",
            answer: `Yes, a harness is necessary. Mantrailing involves your dog working out in front and pulling, which isn't safe on a collar. Any comfortable harness is fine for the introduction workshop.

If you choose to continue with the sport, you can then invest in a suitable Mantrailing-specific harness.`
        },
        {
            question: "I can't run. Will this be a problem in Mantrailing?",
            answer: `Not at all. Running is never required. In fact, walking behind your dog is safer and more effective. Running can cause your dog to pant, which reduces their ability to sniff and follow the trail.

Slow, steady walking is all that's needed.`
        },
        {
            question: "My dog has no recall. Will this be a problem?",
            answer: "No recall is needed at any stage. Dogs are always worked on a long line and are never taken off lead during Mantrailing sessions."
        },
        {
            question: "My dog is nervous of people or other dogs. Can we still do Mantrailing?",
            answer: `For most dogs, yes—Mantrailing can be a fantastic confidence builder.

However, it won't automatically make your dog social. Some dogs may require muzzle training to participate safely, or may start with tracking-style exercises (without a person to find) to build understanding and confidence.

If you're unsure, please contact Laura before booking to discuss your dog's needs. Safety for everyone involved—especially the humans hiding—is always a priority.`
        },
        {
            question: "Can I attend your training sessions if I've done an introduction workshop or trailed with another instructor?",
            answer: `Yes! Learning from different instructors is encouraged and provides a broader experience.

If you're unsure whether your previous training style aligns, feel free to contact Laura before booking.`
        },
        {
            question: "My dog has no training at all. Can they still do Mantrailing?",
            answer: "Absolutely! No prior training or experience is required. All dogs are welcome—even the mischievous, energetic, and full-of-beans ones."
        },
        {
            question: "I already do another sport with my dog (agility, tracking, working trials, scent detection, obedience). Can we still start Mantrailing?",
            answer: `Yes! It is likely that the other sports you do will not interfere at all. However, it is possible that some previously learned behaviours from other dog sports may be either useful or make certain aspects slightly harder. If you wish to discuss your specific scenario please get in touch.`
        },
        {
            question: "Can all breeds do Mantrailing?",
            answer: "Yes! Every breed has scenting ability. While some dogs may progress faster than others, all dogs can enjoy and benefit from Mantrailing."
        }
    ];

    return (
        <div className="min-h-screen" data-testid="faq-page">
            <SEOHead 
                title={pageSEO.faq.title}
                description={pageSEO.faq.description}
                keywords={pageSEO.faq.keywords}
                canonical={pageSEO.faq.canonical}
                schema={[
                    generateFAQSchema(faqs),
                    generateBreadcrumbSchema([
                        { name: 'Home', url: '/' },
                        { name: 'FAQ', url: '/faq' }
                    ])
                ]}
            />
            {/* Hero Section */}
            <section className="relative py-16 md:py-20 bg-primary/5">
                <div className="container-custom">
                    <div className="max-w-3xl mx-auto text-center">
                        <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                            <HelpCircle className="w-4 h-4" /> Got Questions?
                        </div>
                        <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight font-serif">
                            Mantrailing FAQs — Your Questions Answered
                        </h1>
                        <p className="text-xl text-muted-foreground leading-relaxed">
                            Everything you need to know about mantrailing with Craven Canines in Wiltshire. 
                            Can't find your answer? Feel free to get in touch!
                        </p>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="section-spacing" data-testid="faq-section">
                <div className="container-custom">
                    <div className="max-w-3xl mx-auto">
                        <Accordion type="single" collapsible className="space-y-4">
                            {faqs.map((faq, index) => (
                                <AccordionItem 
                                    key={index} 
                                    value={`item-${index}`}
                                    className="bg-white rounded-xl border border-border/50 shadow-sm px-6 data-[state=open]:shadow-md transition-shadow"
                                    data-testid={`faq-item-${index}`}
                                >
                                    <AccordionTrigger className="text-left py-5 hover:no-underline group">
                                        <span className="text-foreground font-semibold pr-4 group-hover:text-primary transition-colors">
                                            {faq.question}
                                        </span>
                                    </AccordionTrigger>
                                    <AccordionContent className="pb-5">
                                        <div className="text-muted-foreground leading-relaxed whitespace-pre-line">
                                            {faq.answer}
                                        </div>
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="section-spacing bg-secondary/30" data-testid="faq-cta">
                <div className="container-custom">
                    <div className="max-w-2xl mx-auto text-center">
                        <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border border-border/50">
                            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                                <MessageCircle className="w-8 h-8 text-primary" />
                            </div>
                            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4 font-serif">
                                Still Have Questions?
                            </h2>
                            <p className="text-muted-foreground mb-8">
                                Can't find the answer you're looking for? I'd love to hear from you. 
                                Drop me a message and I'll get back to you as soon as possible.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Link to="/contact">
                                    <Button className="btn-primary w-full sm:w-auto">
                                        <Mail className="w-4 h-4 mr-2" />
                                        Get in Touch
                                    </Button>
                                </Link>
                                <Link to="/mantrailing">
                                    <Button variant="outline" className="w-full sm:w-auto rounded-full border-primary/30 hover:bg-primary/5">
                                        View Sessions
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default FAQPage;
